import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./RolesPage.css";
import {
  listRoleNotificationUsers,
  bulkRoleUserEventNotifications,
  getNavigationTree,
  getRoleUserNavigation,
  updateRoleUserNavigation,
  resetRoleUserNavigation,
} from "../../services/roleAdmin";
import {
  CHANNELS,
  CHANNEL_STATE,
  CHANNEL_STATE_LABEL,
  normalizeRoleNotificationUsers,
  buildBulkRequests,
  describeBulkResult,
  describeBulkRequest,
  describeBulkProblems,
} from "../../utils/roleNotificationModel";
import {
  normalizeNavigationTree,
  presentNavigationTree,
  normalizeUserNavigation,
  flattenNavigationKeys,
  isNavigationNodePartial,
  toggleNavigationKey,
  sameNavigationSelection,
  describeVisibleTabs,
  withoutAdminOnly,
} from "../../utils/navigationEntitlements";
import { HIERARCHY_LABELS } from "../../utils/hierarchyLabels";
import { openCameraFrames, openLocationInCameras } from "../../utils/camerasDrilldown";
import { describeUserError } from "../../utils/apiError";
import { ResizableColgroup, ResizableTh } from "../../components/ColumnResizeHandle/ColumnResizeHandle";
import { useResizableColumns } from "../../hooks/useResizableColumns";

/*
 * ROLES-NOTIFY01 (ТЗ v4 §29, FE-FIX26) + NAV-ENTITLEMENTS01 (§31, FE-FIX28).
 *
 * «Роли» — центральная admin-поверхность: доступные пользователю локации и
 * камеры, эффективное состояние каналов уведомлений и видимые вкладки. Всё это
 * read model backend. Прежняя версия экрана собирала строки сама, обходя
 * иерархию заводов и вычисляя владельцев на клиенте, — ровно тот frontend
 * aggregation, который ТЗ запрещает: ACL знает только сервер.
 *
 * Изменения каналов staged: пока не нажато «Подтвердить», ничего не уходит.
 * Применяется одним bulk-запросом на направление (включить/выключить), а не
 * запросом на камеру, и с явным confirm. После применения — refetch, чтобы
 * mixed/override пришли из backend, а не из наших догадок.
 */

const LIST_LIMIT = 6;

const errorText = (error, fallback) => describeUserError(error, fallback);

// Версия навигации обязана быть числом: expected_version: null контракт не принимает.
const hasVersion = (navigation) => typeof navigation?.version === "number";

/*
 * onOpen(item) — переход вместо обычного Link, когда он есть: ссылке нужно
 * подготовить позицию в «Камерах» (см. utils/camerasDrilldown). Клик с
 * модификатором (новая вкладка) остаётся обычной ссылкой.
 */
const EntityLinks = ({ items, count, hrefFor, emptyText, onOpen }) => {
  const [expanded, setExpanded] = useState(false);

  if (!items || items.length === 0) {
    return <span className="rp-muted">{count ? `Всего ${count}` : emptyText}</span>;
  }

  const visible = expanded ? items : items.slice(0, LIST_LIMIT);
  const rest = items.length - visible.length;

  return (
    <div className="rp-links">
      <span className="rp-links-count">Всего {count ?? items.length}</span>
      <span className="rp-links-list">
        {visible.map((item) => {
          const href = hrefFor(item);
          return href
            ? (
              <Link
                key={item.id || item.name}
                className="rp-link"
                to={href}
                onClick={(event) => {
                  if (!onOpen || !item.path) return;
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                  event.preventDefault();
                  onOpen(item);
                }}
              >
                {item.name}
              </Link>
            )
            : <span key={item.id || item.name} className="rp-muted">{item.name}</span>;
        })}
      </span>
      {rest > 0 && (
        <button type="button" className="rp-inline-btn" onClick={() => setExpanded(true)}>
          Ещё {rest}
        </button>
      )}
    </div>
  );
};

/**
 * Трёхзначный чекбокс канала. Смысл несёт подпись, а не цвет: mixed и «нет
 * данных» подписаны словами и дополнительно помечены indeterminate.
 */
const ChannelCheckbox = ({ id, label, state, desired, disabled, onChange }) => {
  const ref = useRef(null);
  const staged = typeof desired === "boolean";
  const checked = staged ? desired : state === CHANNEL_STATE.on;
  const indeterminate = !staged && (state === CHANNEL_STATE.mixed || state === CHANNEL_STATE.unknown);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className="rp-channel">
      <label className="rp-channel-main" htmlFor={id}>
        <input
          id={id}
          ref={ref}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{label}</span>
      </label>
      <span className="rp-channel-state">
        {staged ? `Будет ${desired ? "включено" : "выключено"}` : CHANNEL_STATE_LABEL[state]}
      </span>
    </div>
  );
};

/** Дерево «Видимых вкладок»: родитель виден при выборе себя или потомка. */
const NavigationTree = ({ nodes, selected, disabled, onToggle, level = 0 }) => (
  <ul className="rp-nav-tree" style={{ marginLeft: level ? 18 : 0 }}>
    {nodes.map((node) => (
      <li key={node.key}>
        <label className="rp-nav-node">
          <input
            type="checkbox"
            checked={selected.includes(node.key)}
            disabled={disabled}
            onChange={() => onToggle(node)}
          />
          <span>{node.label}</span>
          {isNavigationNodePartial(node, selected) && (
            <span className="rp-nav-partial">видна из-за вложенного пункта</span>
          )}
        </label>
        {node.children?.length > 0 && (
          <NavigationTree
            nodes={node.children}
            selected={selected}
            disabled={disabled}
            onToggle={onToggle}
            level={level + 1}
          />
        )}
      </li>
    ))}
  </ul>
);

const NavigationEditor = ({ row, tree, onClose, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  // Сообщение о конфликте живёт отдельно от error: перечитывание состояния
  // не должно стирать предупреждение, из-за которого оно и запустилось.
  const [conflict, setConflict] = useState("");
  const [saving, setSaving] = useState(false);
  // Состояние, от которого администратор начал правку: после 409 повтор
  // считает diff именно от него, а не от перечитанного чужого состояния.
  const baselineRef = useRef([]);
  // Незавершённый конфликт: намерение администратора и вид операции. Живёт,
  // пока перечитывание не удалось, чтобы «Повторить» не стирало выбор.
  const pendingRef = useRef(null);
  const [notice, setNotice] = useState("");

  const load = useCallback((keepSelection) => {
    setStatus("loading");
    setError("");
    setConflict("");
    setNotice("");
    return getRoleUserNavigation(row.id)
      .then((data) => {
        const navigation = normalizeUserNavigation(data);
        setCurrent(navigation);
        if (!keepSelection) {
          setSelected(navigation.visibleKeys);
          baselineRef.current = navigation.visibleKeys;
        }
        setStatus("ready");
        return navigation;
      })
      .catch((requestError) => {
        setStatus("error");
        setError(errorText(requestError, "Не удалось загрузить видимые вкладки."));
        return null;
      });
  }, [row.id]);

  /*
   * 409: перечитываем свежие ключи и версию, переносим собственное намерение
   * администратора (diff от исходного состояния) на свежий набор. Чужие правки
   * остаются, выбор и baseline становятся «свежие + наши».
   */
  const resolveConflict = async () => {
    const { added, removed, kind } = pendingRef.current;
    const fresh = await load(true);
    if (!fresh) return; // pendingRef остаётся: «Повторить» повторит перечитывание
    pendingRef.current = null;
    if (kind === "reset") {
      setNotice("Набор вкладок изменился. Проверьте его и нажмите «По умолчанию» ещё раз.");
      return;
    }
    const merged = [
      ...fresh.visibleKeys.filter((key) => !removed.includes(key)),
      ...added.filter((key) => !fresh.visibleKeys.includes(key)),
    ];
    setSelected(merged);
    baselineRef.current = fresh.visibleKeys;
    if (sameNavigationSelection(merged, fresh.visibleKeys)) {
      setNotice("Эти изменения уже применены.");
      return;
    }
    setConflict("Пока вы редактировали, вкладки изменил другой администратор. Ваши изменения перенесены на новый набор — проверьте и сохраните ещё раз.");
  };

  const handleConflict = (kind) => {
    const baseline = baselineRef.current;
    pendingRef.current = {
      kind,
      added: selected.filter((key) => !baseline.includes(key)),
      removed: baseline.filter((key) => !selected.includes(key)),
    };
    return resolveConflict();
  };

  useEffect(() => { load(false); }, [load]);

  const immutable = row.isAdmin || Boolean(current?.immutable);
  // admin_only-разделы выдаёт роль, а не галочка: обычному пользователю их не показываем.
  const editableTree = useMemo(() => (immutable ? tree : withoutAdminOnly(tree)), [immutable, tree]);
  const dirty = current ? !sameNavigationSelection(selected, current.visibleKeys) : false;
  const versionReady = hasVersion(current);

  const save = async () => {
    if (!versionReady) return;
    setSaving(true);
    setError("");
    try {
      const baseline = baselineRef.current;
      await updateRoleUserNavigation(row.id, conflict
        ? {
          // Повтор после 409: только намерение администратора, чужие правки остаются.
          expected_version: current.version,
          mode: "diff",
          add_keys: selected.filter((key) => !baseline.includes(key)),
          remove_keys: baseline.filter((key) => !selected.includes(key)),
        }
        : {
          expected_version: current.version,
          mode: "replace",
          visible_keys: selected,
        });
      setConflict("");
      onSaved();
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        // Навигацию поменяли параллельно: чужие правки не затираем.
        await handleConflict("save");
      } else {
        setError(errorText(requestError, "Не удалось сохранить видимые вкладки."));
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!versionReady) return;
    if (!window.confirm("Вернуть пользователю набор вкладок по умолчанию?")) return;
    setSaving(true);
    setError("");
    try {
      await resetRoleUserNavigation(row.id, current.version);
      onSaved();
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        await handleConflict("reset");
      } else {
        setError(errorText(requestError, "Не удалось сбросить видимые вкладки."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rp-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="rp-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Видимые вкладки ${row.email || row.name}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rp-modal-head">
          <h2>Видимые вкладки</h2>
          <span className="rp-muted">{row.email || row.name}</span>
        </div>

        {status === "loading" && <div className="rp-state">Загрузка…</div>}

        {status === "error" && (
          <div className="rp-state rp-state--error">
            {error}
            <button type="button" className="rp-inline-btn" onClick={() => (pendingRef.current ? resolveConflict() : load(false))}>Повторить</button>
          </div>
        )}

        {status === "ready" && (
          <>

            {editableTree.length === 0
              ? <div className="rp-state">Дерево разделов недоступно.</div>
              : (
                <NavigationTree
                  nodes={editableTree}
                  selected={selected}
                  disabled={immutable || saving}
                  onToggle={(node) => setSelected((prev) => toggleNavigationKey(prev, node))}
                />
              )}

            {conflict && <div className="rp-state rp-state--error">{conflict}</div>}
            {notice && <div className="rp-note">{notice}</div>}
            {error && <div className="rp-state rp-state--error">{error}</div>}

            <div className="rp-modal-actions">
              <button
                type="button"
                className="rp-btn rp-btn--primary"
                disabled={immutable || saving || !versionReady || (!dirty && !conflict)}
                onClick={save}
              >
                {conflict ? "Повторить" : "Сохранить"}
              </button>
              <button type="button" className="rp-btn" disabled={immutable || saving || !versionReady} onClick={reset}>
                По умолчанию
              </button>
              <button type="button" className="rp-btn" onClick={onClose}>Закрыть</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CLIENT_COLUMN_KEYS = ["email", "rooms", "cameras", "notifications", "tabs", "actions"];

const RolesPage = () => {
  const navigate = useNavigate();
  const resize = useResizableColumns("roles_clients_widths");
  const [rows, setRows] = useState([]);
  const [tree, setTree] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [staged, setStaged] = useState({});
  const [busyId, setBusyId] = useState("");
  const [rowMessages, setRowMessages] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [navUser, setNavUser] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      const data = await listRoleNotificationUsers();
      setRows(normalizeRoleNotificationUsers(data));
      setStatus("ready");
    } catch (requestError) {
      setError(errorText(requestError, "Не удалось загрузить список пользователей."));
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let mounted = true;
    getNavigationTree()
      .then((data) => { if (mounted) setTree(presentNavigationTree(normalizeNavigationTree(data))); })
      .catch(() => { if (mounted) setTree([]); });
    return () => { mounted = false; };
  }, []);

  const navTotal = useMemo(() => flattenNavigationKeys(withoutAdminOnly(tree)).length, [tree]);

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    const haystack = (row) => [
      row.email,
      row.name,
      ...row.locations.map((item) => item.name),
      ...row.cameras.map((item) => item.name),
    ].join(" ").toLowerCase();
    return rows.filter((row) => haystack(row).includes(needle));
  }, [rows, search]);

  const stageChannel = (row, channelKey, value) => {
    setStaged((prev) => {
      const next = { ...(prev[row.id] || {}) };
      const currentState = row.channels[channelKey];
      const alreadyThere =
        (value && currentState === CHANNEL_STATE.on) || (!value && currentState === CHANNEL_STATE.off);
      if (alreadyThere) delete next[channelKey];
      else next[channelKey] = value;

      const result = { ...prev };
      if (Object.keys(next).length === 0) delete result[row.id];
      else result[row.id] = next;
      return result;
    });
    setRowErrors((prev) => ({ ...prev, [row.id]: "" }));
  };

  const cancelStaged = (row) => {
    setStaged((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    setRowErrors((prev) => ({ ...prev, [row.id]: "" }));
  };

  const applyStaged = async (row) => {
    const requests = buildBulkRequests(staged[row.id], row.channels);
    if (requests.length === 0) return;

    const confirmed = window.confirm(
      `Применить настройку уведомлений для ${row.email || row.name}?\n`
      + "Изменение затронет все доступные пользователю камеры и перекроет ручные настройки на камерах."
    );
    if (!confirmed) return;

    setBusyId(row.id);
    setRowErrors((prev) => ({ ...prev, [row.id]: "" }));
    setRowMessages((prev) => ({ ...prev, [row.id]: [] }));
    const lines = [];
    const doneKeys = [];
    let failure = "";
    // Одно направление — один bulk, запросы идут по очереди. Если второй
    // упал, итог первого остаётся на экране: частичный успех не теряем.
    for (const body of requests) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await bulkRoleUserEventNotifications(row.id, body);
        const counters = describeBulkResult(result);
        lines.push(
          counters ? `${describeBulkRequest(body)}. ${counters}` : describeBulkRequest(body),
          ...describeBulkProblems(result),
        );
        CHANNELS.forEach(({ key, param }) => { if (body[param]) doneKeys.push(key); });
      } catch (requestError) {
        failure = errorText(requestError, "Не удалось применить настройку.");
        break;
      }
    }
    setRowMessages((prev) => ({ ...prev, [row.id]: lines }));
    if (failure) setRowErrors((prev) => ({ ...prev, [row.id]: failure }));
    // Применённые каналы снимаем со staged, неприменённые остаются для повтора.
    setStaged((prev) => {
      const next = { ...(prev[row.id] || {}) };
      doneKeys.forEach((key) => delete next[key]);
      const result = { ...prev };
      if (Object.keys(next).length === 0) delete result[row.id];
      else result[row.id] = next;
      return result;
    });
    // Показываем состояние backend, а не наши догадки.
    await load();
    setBusyId("");
  };

  const renderRow = (row) => {
    const stagedRow = staged[row.id];
    const requests = buildBulkRequests(stagedRow, row.channels);
    const busy = busyId === row.id;

    return (
      <tr key={row.id} className={row.isAdmin ? "rp-row--admin" : undefined}>
        <td>
          <div className="rp-user">{row.email || row.name}</div>
          {row.isAdmin && <span className="rp-tag">Администратор</span>}
        </td>
        <td>
          <EntityLinks
            items={row.locations}
            count={row.locationCount}
            hrefFor={() => "/cameras"}
            onOpen={(item) => openLocationInCameras(navigate, item.path)}
            emptyText={`${HIERARCHY_LABELS.room.plural} недоступны`}
          />
        </td>
        <td>
          <EntityLinks
            items={row.cameras}
            count={row.cameraCount}
            hrefFor={(item) => (item.id ? (item.path ? "/cameras" : `/cameras/${item.id}/diagnostics`) : "")}
            onOpen={(item) => openCameraFrames(navigate, item, item.path)}
            emptyText={`${HIERARCHY_LABELS.camera.plural} недоступны`}
          />
        </td>
        <td>
          <div className="rp-channels">
            {CHANNELS.map((channel) => (
              <ChannelCheckbox
                key={channel.key}
                id={`rp-${row.id}-${channel.key}`}
                label={channel.label}
                state={row.channels[channel.key]}
                desired={stagedRow?.[channel.key]}
                disabled={busy}
                onChange={(value) => stageChannel(row, channel.key, value)}
              />
            ))}
          </div>
        </td>
        <td>
          <div className="rp-nav-cell">
            <span>
              {row.isAdmin
                ? "Все вкладки (неизменяемо)"
                : row.navigation.visibleKeys
                  ? describeVisibleTabs(row.navigation.visibleKeys, row.navigation.total ?? navTotal)
                  : ""}
            </span>
            <button type="button" className="rp-inline-btn" onClick={() => setNavUser(row)}>
              {row.isAdmin ? "Посмотреть" : "Настроить"}
            </button>
          </div>
        </td>
        <td>
          <div className="rp-row-actions">
            <button
              type="button"
              className="rp-btn rp-btn--primary"
              disabled={requests.length === 0 || busy}
              onClick={() => applyStaged(row)}
            >
              {busy ? "Применяем…" : "Подтвердить"}
            </button>
            <button
              type="button"
              className="rp-btn"
              disabled={!stagedRow || busy}
              onClick={() => cancelStaged(row)}
            >
              Отменить
            </button>
          </div>
          {(rowMessages[row.id] || []).map((line, index) => <div key={index} className="rp-row-msg">{line}</div>)}
          {rowErrors[row.id] && <div className="rp-row-msg rp-row-msg--error">{rowErrors[row.id]}</div>}
        </td>
      </tr>
    );
  };

  return (
    <div className="rp-page">
      <div className="rp-table-shell">
        <div className="rp-table-toolbar">
          <label>
            Поиск
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Email, локация, камера"
            />
          </label>
          <span>{visibleRows.length} из {rows.length}</span>
        </div>

        {status === "loading" && <div className="rp-state">Загрузка…</div>}

        {status === "error" && (
          <div className="rp-state rp-state--error">
            {error}
            <button type="button" className="rp-inline-btn" onClick={load}>Повторить</button>
          </div>
        )}

        {status === "ready" && (
          <div className="rp-table-wrap">
            <table {...resize.tableProps(CLIENT_COLUMN_KEYS, "rp-clients-table")}>
              <ResizableColgroup resize={resize} keys={CLIENT_COLUMN_KEYS} />
              <thead>
                <tr>
                  <ResizableTh resize={resize} colKey="email">Email</ResizableTh>
                  <ResizableTh resize={resize} colKey="rooms" label={HIERARCHY_LABELS.room.plural} />
                  <ResizableTh resize={resize} colKey="cameras" label={HIERARCHY_LABELS.camera.plural} />
                  <ResizableTh resize={resize} colKey="notifications">Уведомления о событиях</ResizableTh>
                  <ResizableTh resize={resize} colKey="tabs">Видимые вкладки</ResizableTh>
                  <ResizableTh resize={resize} colKey="actions">Действия</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(renderRow)}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="rp-empty-cell">
                      {rows.length === 0 ? "Пользователи не найдены." : "По поиску ничего не найдено."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {navUser && (
        <NavigationEditor
          key={navUser.id}
          row={navUser}
          tree={tree}
          onClose={() => setNavUser(null)}
          onSaved={() => { setNavUser(null); load(); }}
        />
      )}
    </div>
  );
};

export default RolesPage;
